import { Box, Divider, IconButton, Stack } from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useMemo } from "react";
import { Button, Panel, Typography } from "@platform/design-system";

import {
  useBrokerSummaryWithCarrierQuery,
  useCarrierRuleSelectionQuery,
} from "../../../../src/hooks/useBbe";
import { CarrierName } from "../constants";

import { BonusSummaryDescription } from "./BonusSummaryDescription";

type CarrierRulesDrawerProps = {
  setOpen: (value: boolean) => void;
  open: boolean;
  brokerId: string;
  carrierId: string;
  carrierName: CarrierName;
};

export const CarrierRulesDrawer: React.FC<CarrierRulesDrawerProps> = ({
  setOpen,
  open,
  brokerId,
  carrierId,
  carrierName,
}) => {
  const { data: brokerSummary } = useBrokerSummaryWithCarrierQuery(brokerId, {
    enabled: open,
  });
  const { data: carrierRulesData } = useCarrierRuleSelectionQuery({
    enabled: open,
  });

  const carrierEntry = brokerSummary?.find(
    (broker) => broker.carrierId === carrierId,
  );
  const brokerSummaries = carrierEntry?.brokerSummaries ?? [];
  const filteredBrokerSummary =
    brokerSummaries.find((summary) => summary.isCurrent) ?? brokerSummaries[0];

  const filteredCarrierRule = useMemo(
    () =>
      carrierRulesData?.find(
        (rule) => rule.carrierRuleId === filteredBrokerSummary?.carrierRuleId,
      ),
    [carrierRulesData, filteredBrokerSummary?.carrierRuleId],
  );

  const beginningBookSize = filteredBrokerSummary?.beginningBookSize;

  return (
    <Panel
      open={open}
      anchor="right"
      sx={{
        position: "absolute",
        right: "-44px",
        top: "-24px",
        bottom: "-24px",
      }}
    >
      <Stack
        sx={{
          height: "100%",
          justifyContent: "space-between",
          overflow: "hidden",
        }}
      >
        <Stack spacing={0} flexShrink={0}>
          <Stack
            p={2}
            direction="row"
            spacing={0}
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body-large" color="textSecondary">
              Carrier Bonus Rules Summary
            </Typography>

            <IconButton
              size="medium"
              onClick={() => setOpen(false)}
              aria-label="close-drawer"
            >
              <CloseOutlinedIcon />
            </IconButton>
          </Stack>
          <Divider />
        </Stack>

        <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          <Stack spacing={2} p={2}>
            <Typography variant="header-medium">
              {filteredCarrierRule?.ruleTitle}
            </Typography>

            <BonusSummaryDescription
              carrierName={carrierName}
              currentRuleId={filteredCarrierRule?.ruleId}
              startOfYearPremiumInput={beginningBookSize}
              layout="stack"
              bonusYear={filteredCarrierRule?.ruleYear}
            />
          </Stack>
        </Box>
        <Stack spacing={1} flexShrink={0}>
          <Divider />
          <Box display="flex" justifyContent="flex-end" px={2}>
            <Button size="lg" variant="tertiary" onClick={() => setOpen(false)}>
              CLOSE
            </Button>
          </Box>
        </Stack>
      </Stack>
    </Panel>
  );
};
